import { useEffect, useState } from "react";
import { HeartHandshake, CalendarDays, Wallet, Hourglass, Users, TrendingUp, Cake, PartyPopper, Package, Sparkles } from "lucide-react";
import Horse from "@/components/icons/Horse";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import api from "@/lib/api";
import DashboardShortcuts from "@/components/DashboardShortcuts";
import { useAuth } from "@/context/AuthContext";
import { ROLE_LABELS } from "@/config/navigation";

const KPI_CARDS = [
  { key: "praticantes_ativos", label: "Praticantes ativos", icon: HeartHandshake, tone: "turquoise" },
  { key: "avaliacoes_hoje", label: "Avaliações hoje", icon: Sparkles, tone: "lilac" },
  { key: "sessoes_hoje", label: "Sessões hoje", icon: CalendarDays, tone: "gold" },
  { key: "cavalos_ativos", label: "Cavalos ativos", icon: Horse, tone: "emerald" },
  { key: "lista_espera", label: "Lista de espera", icon: Hourglass, tone: "pink" },
  { key: "estoque_abaixo_minimo", label: "Estoque baixo", icon: Package, tone: "amber" },
];

const TONE_MAP = {
  turquoise: "bg-[#DFF4F5] text-[#3A9CA2]",
  gold: "bg-[#FBEEDA] text-[#B98A3E]",
  pink: "bg-[#FBE1EC] text-[#C4547E]",
  lilac: "bg-[#EDE1F3] text-[#7B569A]",
  emerald: "bg-emerald-50 text-emerald-700",
  amber: "bg-amber-50 text-amber-700",
};

export default function Dashboard() {
  const { user } = useAuth();
  const [kpis, setKpis] = useState(null);
  const [welcome, setWelcome] = useState(null);

  useEffect(() => {
    let alive = true;
    Promise.all([
      api.get("/dashboard/kpis"),
      api.get("/dashboard/welcome"),
    ]).then(([k, w]) => {
      if (!alive) return;
      setKpis(k.data);
      setWelcome(w.data);
    }).catch(() => alive && setKpis({}));
    return () => { alive = false; };
  }, []);

  const today = new Date().toLocaleDateString("pt-BR", {
    weekday: "long", day: "2-digit", month: "long", year: "numeric",
  });

  const saudacao = welcome?.saudacao || "Olá";
  const firstName = (welcome?.nome || user.name || "").split(" ")[0];
  const aniversariantes = welcome?.aniversariantes || [];

  return (
    <div data-testid="dashboard-page" className="space-y-8">
      {/* Welcome hero */}
      <div
        className="rounded-2xl p-8 border border-border relative overflow-hidden"
        style={{
          background: "linear-gradient(120deg, #EAF7F8 0%, #FDF3E7 55%, #FCE7F0 100%)",
        }}
      >
        <div className="relative z-10">
          <p className="text-xs uppercase tracking-widest text-brand-lilac font-semibold">{today}</p>
          <h1 className="mt-1 font-heading text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
            {saudacao}, <span className="text-brand-turquoise">{firstName}</span> <span className="inline-block animate-wave">👋</span>
          </h1>
          <div className="mt-2 text-slate-600 max-w-2xl">
            Bem-vindo(a) ao painel do <span className="font-semibold text-slate-800">Anjos de Patas</span>. Perfil: <Badge variant="secondary" className="ml-1">{ROLE_LABELS[user.role]}</Badge>
          </div>
        </div>
        <img src="/brand/logo.webp" alt="" className="absolute -right-6 -bottom-8 w-56 h-56 object-contain opacity-30 pointer-events-none" />
      </div>

      {/* Birthdays alert */}
      {aniversariantes.length > 0 && (
        <Card data-testid="dashboard-birthdays" className="border-brand-pink/40 bg-[#FEF5F8] rounded-xl">
          <CardContent className="p-5">
            <div className="flex items-start gap-4">
              <div className="h-11 w-11 rounded-full bg-brand-pink/30 flex items-center justify-center shrink-0">
                <PartyPopper className="h-5 w-5 text-brand-lilac" />
              </div>
              <div className="flex-1">
                <h3 className="font-heading font-bold text-slate-900">
                  Aniversariantes de hoje 🎂
                </h3>
                <p className="text-sm text-slate-600 mt-1">
                  Não esqueça de dar os parabéns:
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {aniversariantes.map((a) => (
                    <div
                      key={`${a.tipo}-${a.id}`}
                      className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-brand-pink/40 shadow-sm"
                    >
                      <Cake className="h-3.5 w-3.5 text-brand-pink" />
                      <span className="text-sm font-medium text-slate-800">{a.nome}</span>
                      <span className="text-[11px] text-muted-foreground">
                        · {a.tipo}{a.idade ? ` · ${a.idade} anos` : ""}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Atalhos por perfil */}
      <DashboardShortcuts role={user.role} />

      {/* KPI grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        {KPI_CARDS.map((kpi) => {
          const Icon = kpi.icon;
          return (
            <Card
              key={kpi.key}
              data-testid={`kpi-card-${kpi.key.replace(/_/g, "-")}`}
              className="border-border shadow-none hover:shadow-md transition-shadow rounded-xl bg-white"
            >
              <CardContent className="p-5">
                <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${TONE_MAP[kpi.tone]}`}>
                  <Icon className="h-5 w-5" strokeWidth={2} />
                </div>
                <p className="mt-4 text-2xl font-heading font-bold text-slate-900">
                  {kpis ? (kpis[kpi.key] ?? 0).toLocaleString("pt-BR") : "—"}
                </p>
                <p className="mt-1 text-sm text-muted-foreground">{kpi.label}</p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Two-column placeholder */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2 border-border shadow-none rounded-xl bg-white">
          <CardContent className="p-8">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="font-heading text-xl font-semibold text-slate-800">Próximas sessões</h2>
                <p className="text-sm text-muted-foreground mt-1">A agenda inteligente será liberada na próxima entrega.</p>
              </div>
              <CalendarDays className="h-6 w-6 text-brand-turquoise" />
            </div>
            <div className="mt-8 flex flex-col items-center justify-center py-12 border border-dashed border-border rounded-lg bg-muted/30">
              <CalendarDays className="h-10 w-10 text-muted-foreground/40" />
              <p className="mt-3 text-sm font-medium text-slate-600">Nenhuma sessão agendada</p>
              <p className="mt-1 text-xs text-muted-foreground">Os agendamentos aparecerão aqui.</p>
            </div>
          </CardContent>
        </Card>

        <Card className="border-border shadow-none rounded-xl bg-white">
          <CardContent className="p-8">
            <div className="flex items-center justify-between">
              <h2 className="font-heading text-xl font-semibold text-slate-800">Evolução mensal</h2>
              <TrendingUp className="h-6 w-6 text-brand-turquoise" />
            </div>
            <div className="mt-8 flex flex-col items-center justify-center py-12 border border-dashed border-border rounded-lg bg-muted/30">
              <TrendingUp className="h-10 w-10 text-muted-foreground/40" />
              <p className="mt-3 text-sm font-medium text-slate-600">Sem dados por enquanto</p>
              <p className="mt-1 text-xs text-muted-foreground">Relatórios chegam na próxima fase.</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
